import { Events } from '../events';

interface Tool {
    activate: () => void;
    deactivate: () => void;
}

class ToolManager {
    tools = new Map<string, Tool>();
    events: Events;
    active: string | null = null;
    // presentation (?view) mode: no editing tools may be activated, so a shared
    // experience can't be moved/rotated/scaled or re-selected by the viewer
    locked = false;

    constructor(events: Events) {
        this.events = events;
        this.locked = !!events.invoke('viewMode');

        this.events.on('tool.deactivate', () => {
            this.activate(null);
        });

        this.events.function('tool.active', () => {
            return this.active;
        });

        let coordSpace: 'local' | 'world' = 'world';

        const setCoordSpace = (space: 'local' | 'world') => {
            if (space !== coordSpace) {
                coordSpace = space;
                events.fire('tool.coordSpace', coordSpace);
            }
        };

        events.function('tool.coordSpace', () => {
            return coordSpace;
        });

        events.on('tool.setCoordSpace', (value: 'local' | 'world') => {
            setCoordSpace(value);
        });

        events.on('tool.toggleCoordSpace', () => {
            setCoordSpace(coordSpace === 'local' ? 'world' : 'local');
        });

        let snap = false;

        const setSnap = (value: boolean) => {
            if (value !== snap) {
                snap = value;
                events.fire('tool.snap', snap);
            }
        };

        events.function('tool.snap', () => {
            return snap;
        });

        events.on('tool.setSnap', (value: boolean) => {
            setSnap(value);
        });

        events.on('tool.toggleSnap', () => {
            setSnap(!snap);
        });
    }

    register(name: string, tool: Tool) {
        this.tools.set(name, tool);

        this.events.on(`tool.${name}`, () => {
            this.activate(name);
        });
    }

    get(toolName: string) {
        return (toolName && this.tools.get(toolName)) ?? null;
    }

    activate(toolName: string | null) {
        // never activate an editing tool in presentation mode
        if (this.locked && toolName) {
            return;
        }
        if (toolName === this.active) {
            // re-activating the currently active tool deactivates it
            if (toolName) {
                this.activate(null);
            }
        } else {
            // deactive old tool
            if (this.active) {
                const tool = this.tools.get(this.active);
                tool.deactivate();
                this.events.fire(`tool.${this.active}.deactivated`);
                this.events.fire('tool.deactivated', this.active);
            }

            this.active = toolName;

            // activate the new
            if (this.active) {
                const tool = this.tools.get(this.active);
                tool.activate();
            }

            this.events.fire(`tool.${toolName}.activated`);
            this.events.fire('tool.activated', toolName);
        }
    }
}

export { ToolManager };
